<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="closeModal">
    <div class="modal" role="dialog" aria-modal="true" :aria-label="$t('player.profile.swap.title')">
      <span class="modal-corner modal-corner--tl" aria-hidden="true"></span>
      <span class="modal-corner modal-corner--tr" aria-hidden="true"></span>
      <span class="modal-corner modal-corner--bl" aria-hidden="true"></span>
      <span class="modal-corner modal-corner--br" aria-hidden="true"></span>

      <div class="header">
        <div class="header-top">
          <CodexHeading :label="$t('player.profile.swap.title')" tone="cyan" spine="head"/>
          <button
              @click="closeModal"
              class="close-btn"
              type="button"
              :title="$t('player.common.close')"
              :aria-label="$t('player.common.close')"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>
        <p class="modal-description">{{ $t('player.profile.swap.hint') }}</p>
      </div>

      <!-- From Stat and To Stat Selection Row -->
      <div class="stat-row">
        <!-- From Stat -->
        <div class="stat-item">
          <label for="fromStat" class="stat-label">{{ $t('player.profile.swap.from') }}</label>
          <select
              id="fromStat"
              v-model="fromStatId"
              class="filter-select"
              @change="updateSelectedFromStat"
          >
            <option
                v-for="stat in stats"
                :key="stat.id"
                :value="stat.id"
            >
              {{ stat.name }}
            </option>
          </select>
          <div class="dice-wrapper" v-if="selectedFromStat">
            <DiceNRolls
                :dice-rolls="selectedFromStat.dice_rolls"
                :stat="selectedFromStat.name"
                :wide="true"
                class="base-value"
            />
          </div>
        </div>

        <span class="swap-arrow" aria-hidden="true">⟶</span>

        <!-- To Stat -->
        <div class="stat-item">
          <label for="toStat" class="stat-label">{{ $t('player.profile.swap.to') }}</label>
          <select
              id="toStat"
              v-model="toStatId"
              class="filter-select"
              @change="updateSelectedToStat"
          >
            <option
                v-for="stat in stats"
                :key="stat.id"
                :value="stat.id"
            >
              {{ stat.name }}
            </option>
          </select>
          <div class="dice-wrapper" v-if="selectedToStat">
            <DiceNRolls
                :dice-rolls="selectedToStat.dice_rolls"
                :stat="selectedToStat.name"
                :wide="true"
                class="base-value"
            />
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="modal-actions">
        <HudButton :type="ButtonType.CANCEL" @click="closeModal">
          {{ $t('player.common.cancel') }}
        </HudButton>
        <HudButton :type="ButtonType.SUBMIT" :disabled="!fromStatId || !toStatId" @click="confirmSwitch">
          {{ $t('player.profile.swap.confirm') }}
        </HudButton>
      </div>
    </div>
  </div>
</template>

<script>
import DiceNRolls from "@/components/Dice/DiceNRolls.vue";
import CodexHeading from "@/components/CodexRail/CodexHeading.vue";
import HudButton from "@/components/btn/HudButton.vue";
import { ButtonType } from "@/types/ButtonType";

export default {
  name: "SwitchBaseValuesModal",
  components: {
    CodexHeading,
    DiceNRolls,
    HudButton,
  },
  props: {
    stats: {
      type: Array,
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    initialFromStatId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      // Options API templates only see the instance, so the enum has to live on it.
      ButtonType,
      fromStatId: this.initialFromStatId,
      toStatId: null,
    };
  },
  watch: {
    initialFromStatId(newVal) {
      this.fromStatId = newVal;
    },
    isOpen(open) {
      if (open) {
        window.addEventListener("keydown", this.onKeydown);
      } else {
        window.removeEventListener("keydown", this.onKeydown);
      }
    },
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown);
  },
  computed: {
    selectedFromStat() {
      return this.stats.find((stat) => stat.id === this.fromStatId);
    },
    selectedToStat() {
      return this.stats.find((stat) => stat.id === this.toStatId);
    },
  },
  methods: {
    onKeydown(event) {
      if (event.key === "Escape") {
        event.stopPropagation();
        this.closeModal();
      }
    },
    updateSelectedFromStat() {
      if (this.toStatId === this.fromStatId) {
        this.toStatId = null;
      }
    },
    updateSelectedToStat() {
      if (this.toStatId === this.fromStatId) {
        this.toStatId = null;
      }
    },
    confirmSwitch() {
      this.$emit("switchBaseValues", {
        fromStatId: this.fromStatId,
        toStatId: this.toStatId,
      });
    },
    closeModal() {
      this.$emit("closeModal");
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  z-index: 9100;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 1rem;
  background: radial-gradient(ellipse at center, rgba(3, 11, 16, 0.74) 0%, rgba(2, 6, 9, 0.93) 100%);
  backdrop-filter: blur(6px) saturate(0.85);
  -webkit-backdrop-filter: blur(6px) saturate(0.85);
}

.modal {
  --hud-cyan: #3ee9f0;
  --hud-cyan-dim: rgba(62, 233, 240, 0.45);
  --hud-line: rgba(0, 255, 255, 0.18);
  --hud-slab: rgba(11, 18, 24, 0.72);
  --hud-text: #d3e0e6;
  --hud-text-dim: #7f929c;
  --codex-spine-w: 14px;
  --codex-spine-gap: 0.9rem;
  --codex-spine: rgba(96, 166, 184, 0.85);

  position: relative;
  width: 100%;
  max-width: 34rem;
  padding: 1.1rem 1.35rem 1.35rem;
  color: var(--hud-text);
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  background: linear-gradient(160deg, rgba(12, 19, 25, 0.97) 0%, rgba(6, 10, 14, 0.99) 100%);
  border: 1px solid var(--hud-line);
  border-radius: 0.375rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.75);
}

.modal-corner {
  position: absolute;
  width: 0.75rem;
  height: 0.75rem;
  border: 1px solid var(--hud-cyan-dim);
  pointer-events: none;
}

.modal-corner--tl { top: -1px; left: -1px; border-width: 1px 0 0 1px; }
.modal-corner--tr { top: -1px; right: -1px; border-width: 1px 1px 0 0; }
.modal-corner--bl { bottom: -1px; left: -1px; border-width: 0 0 1px 1px; }
.modal-corner--br { bottom: -1px; right: -1px; border-width: 0 1px 1px 0; }

.header {
  margin-bottom: 1.25rem;
}

.header-top {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.6rem;
}

.header-top > :first-child {
  flex: 1 1 auto;
  min-width: 0;
}

.close-btn {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  color: var(--hud-text-dim);
  font-size: 0.7rem;
  line-height: 1;
  background: rgba(13, 13, 13, 0.82);
  border: 1px solid var(--hud-line);
  border-radius: 0.25rem;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
}

.close-btn:hover {
  color: var(--hud-cyan);
  background: rgba(62, 233, 240, 0.08);
  border-color: var(--hud-cyan-dim);
}

.close-btn:focus-visible,
.filter-select:focus-visible {
  outline: 1px solid var(--hud-cyan);
  outline-offset: 2px;
}

.modal-description {
  margin: 0;
  font-size: 0.74rem;
  line-height: 1.4;
  color: var(--hud-text-dim);
}

.stat-row {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-item {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.stat-label {
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.54rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--hud-text-dim);
}

.swap-arrow {
  align-self: center;
  padding-top: 1rem;
  color: var(--hud-cyan-dim);
  font-size: 1rem;
}

.filter-select {
  width: 100%;
  padding: 0.4rem 0.55rem;
  color: var(--hud-text);
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  font-size: 0.78rem;
  background: var(--hud-slab);
  border: 1px solid var(--hud-line);
  border-radius: 0.2rem;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.filter-select:focus {
  outline: none;
  background: rgba(16, 26, 34, 0.9);
  border-color: var(--hud-cyan-dim);
}

.filter-select option {
  color: var(--hud-text);
  background: #0a1014;
}

.dice-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  min-height: 3rem;
  padding-right: 1rem;
}

.dice-wrapper :deep(.dice) {
  color: #cfe9ee;
  font-family: 'IBM Plex Mono', ui-monospace, Menlo, monospace;
  background: linear-gradient(135deg, #16323b, #0c1a20);
  border: 1px solid rgba(62, 233, 240, 0.4);
}

.dice-wrapper :deep(.dice.faded) {
  color: #5d7076 !important;
  background: linear-gradient(135deg, #131b20, #0a1014) !important;
  border: 1px solid rgba(125, 146, 156, 0.35);
}

.dice-wrapper :deep(.dice.summary) {
  color: var(--hud-cyan);
  background: linear-gradient(135deg, #12262d, #08131a);
  border: 1px solid rgba(62, 233, 240, 0.55);
}

.modal-actions {
  display: flex;
  gap: 0.6rem;
  justify-content: flex-end;
}


@media (max-width: 640px) {
  .stat-row {
    flex-direction: column;
    gap: 1rem;
  }

  .swap-arrow {
    align-self: center;
    padding-top: 0;
    transform: rotate(90deg);
  }

  .modal-actions {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
  }
}
</style>
